/**
 * Define helper functions for validation forking and common rules
 *  that will be used in more than one form to reduce repetition.
 *  Especially since forking rules makes them so long
 */

import * as yup from "yup";
import moment from "moment";
import { Nullable } from "../types/common";
import { ApprovalStatus } from "../types/approval-status/approval-status.type";

const statuses = [
  "draft",
  "in_progress",
  "work_can_start",
  "ready_for_creative_review",
  "readyForApproval",
  "approved",
] as ApprovalStatus[];

const minDate = () => moment().format("YYYY-MM-DD");
const minDateError = "The date cant' be before today.";
const requiredStartDateMessage = "Please enter a start date.";

// Helper Functions
export const inProgress = (status: ApprovalStatus) => {
  return statuses.indexOf(status) >= 1;
};

export const inProgressWithBoolParent = (status: ApprovalStatus, parentSet: boolean) => {
  return inProgress(status) && parentSet;
};

export const inProgressSimpleArrayRule = (type: any, message: string) =>
  yup.array().when("status", {
    is: inProgress,
    then: yup.array().of(type).min(1, message).required(message),
    otherwise: yup.array().of(type),
  });

export const inProgressDateRule = (message: string) =>
  yup.date().when("status", {
    is: inProgress,
    then: genericDateRule.required(message),
    otherwise: genericDateRule,
  });

export const inProgressStringRule = (message: string) =>
  yup.string().when("status", {
    is: inProgress,
    then: requiredStringRule(message),
    otherwise: yup.string().nullable(),
  });

export const inProgressWithBoolParentNumberRule = (parent: string, message: string) =>
  yup.number().when(["status", parent], {
    is: inProgressWithBoolParent,
    then: nullableNumberRule.required(message),
    otherwise: nullableNumberRule,
  });

export const inProgressWithBoolParentStringRule = (parent: string, message: string) =>
  yup.string().when(["status", parent], {
    is: inProgressWithBoolParent,
    then: yup.string().nullable().required(message),
    otherwise: yup.string().nullable(),
  });

export const nullableNumberRule = yup
  .number()
  .transform((curr) => parseInt(curr, 10) || null)
  .nullable()
  .positive("You must enter a positive number.");

export const regionsRule = yup.array().when(["status", "isRegional"], {
  is: inProgressWithBoolParent,
  then: yup
    .array()
    .of(yup.number())
    .min(1, "You must select at least one region.")
    .required("You must select at least one region."),
  otherwise: yup.array().of(yup.number()),
});

const emptyStringToNullValue = (
  curr: string | null | Date,
  orig: string | null | Date,
  nullValue?: null
) => (orig === "" ? nullValue : curr);

const emptyStringToNull = (curr: string | null | Date, orig: string | null | Date) =>
  emptyStringToNullValue(curr, orig, null);

export const emptyStringToUndefined = (curr: string | null | Date, orig: string | null | Date) =>
  emptyStringToNullValue(curr, orig);

export const requiredDateRule = (errorMessage: string) =>
  yup.date().nullable().transform(emptyStringToNull).required(errorMessage);

export const requiredStringRule = (message: string) => yup.string().nullable().required(message);

export const genericDateRule = yup.date().nullable().transform(emptyStringToUndefined);

export const requiredStartDate = genericDateRule
  .required(requiredStartDateMessage)
  .min(minDate(), minDateError);

export const startDateValidationRule = requiredDateRule(requiredStartDateMessage);

export const endDateValidationRule = requiredDateRule("Please enter an end date.").min(
  yup.ref("startAt"),
  "End date cannot occur prior to start date."
);

const isChangingFromInProgressToDraft = (
  currentStatus: ApprovalStatus,
  newStatus: ApprovalStatus
): boolean => currentStatus === "in_progress" && newStatus === "draft";

export const errorMessageForDatesValidation = (
  startAt: Nullable<string>,
  endAt: Nullable<string>,
  currentStatus: ApprovalStatus,
  newStatus: ApprovalStatus
): Nullable<string> => {
  if (isChangingFromInProgressToDraft(currentStatus, newStatus)) {
    return null;
  }

  if (startAt !== null && endAt !== null && endAt < startAt) {
    return "End date cannot be earlier than start date.";
  }

  return null;
};
